<script lang="ts">
  import { capitalize } from "lodash";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import NotesAndBackup from "@retailer/svelte/Shared/NotesAndBackup.svelte";
  import {
    formatOrderFulfillmentDate,
    shortMonthFormatOrderFulfillmentDate,
    convert24HourTimeTo12Hour,
  } from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import type { SharedOrder, WarehouseSummaryType } from "@shared/types/Order";
  import type { Warehouse } from "@shared/types/Warehouse";
  import { getOrderStockSite } from "@modern/services/retailer/warehouse_service";
  import formatPhone from "@shared/filters/phone.js";

  export let warehouseSummary: WarehouseSummaryType;
  export let order: SharedOrder;

  const OHLQOrderPaymentMethodsEnabled = stagedRolloutCheck(
    "marketplace__ohlq_order_payment_methods",
  );

  const pickupTitle = (stockSite: Warehouse): string => {
    if (OHLQOrderPaymentMethodsEnabled && stockSite) {
      return `Pickup at ${stockSite.formatted_name}`;
    }
    return "In-store pickup";
  };

  const formatPaymentMethod = (paymentMethod: string) => {
    if (paymentMethod == "eft") return paymentMethod.toUpperCase();
    return capitalize(paymentMethod.replace("_", " "));
  };
</script>

{#await getOrderStockSite(OHLQOrderPaymentMethodsEnabled && order.distributor.uses_wine_cellar, order.distributor.id, warehouseSummary.warehouse_id, warehouseSummary.location_code) then stockSite}
  <div
    data-testid="products-header-{warehouseSummary.warehouse_id || 'delivery'}"
    class="fizz-surface fizz-inset-square-16 fizz-flow-16"
    class:fizz-border-bottom={$LayoutStore.isDesktop}
  >
    <div class="fizz-text-large">
      <span class="fizz-text-strong"
        >{warehouseSummary.for_pickup ? pickupTitle(stockSite) : "Delivery"}</span
      >
      <span class="fizz-text-subdued"
        >({pluralize("item", warehouseSummary.total_items, true)})</span
      >
    </div>
    {#if warehouseSummary.warehouse_name || warehouseSummary.fulfillment_date}
      <div
        class="fizz-flex-row-responsive"
        class:fizz-text-medium={$LayoutStore.isDesktop}
        class:fizz-text-small={$LayoutStore.isMobile}
        class:fizz-flow-4={$LayoutStore.isMobile && !OHLQOrderPaymentMethodsEnabled}
      >
        {#if warehouseSummary.for_pickup}
          <span
            data-testid="location-section"
            class="fizz-flex-item fizz-align-self-start"
            class:fizz-flex-row-8={$LayoutStore.isMobile}
          >
            {#if OHLQOrderPaymentMethodsEnabled && stockSite}
              <div class="fizz-text-strong">{stockSite.formatted_name}</div>
              <div>{stockSite.formatted_address}</div>
              {#if stockSite.phone}
                <div>{formatPhone(stockSite.phone)}</div>
              {/if}
            {:else}
              <div class="fizz-text-strong">Location:</div>
              <div>
                {warehouseSummary.warehouse_name}
              </div>
            {/if}
          </span>
        {/if}
        {#if warehouseSummary.fulfillment_date}
          <span
            data-testid="fulfillment-date-section"
            class="fizz-flex-item fizz-align-self-start"
            class:fizz-flex-row-8={$LayoutStore.isMobile}
          >
            <div class="fizz-text-strong">
              {warehouseSummary.for_pickup ? "Pickup" : "Delivery"} date
            </div>
            <div>
              {OHLQOrderPaymentMethodsEnabled
                ? shortMonthFormatOrderFulfillmentDate(warehouseSummary.fulfillment_date)
                : formatOrderFulfillmentDate(warehouseSummary.fulfillment_date)}
              {#if OHLQOrderPaymentMethodsEnabled && warehouseSummary.fulfillment_time}
                <span data-testid="fulfillment-time-section"
                  >at {convert24HourTimeTo12Hour(warehouseSummary.fulfillment_time)}</span
                >
              {/if}
            </div>
          </span>
        {/if}
        {#if OHLQOrderPaymentMethodsEnabled && order.intended_payment_method}
          <span
            data-testid="order-payment-method-section"
            class="fizz-flex-item fizz-align-self-start"
            class:fizz-flex-row-8={$LayoutStore.isMobile}
          >
            <div class="fizz-text-strong"> Payment method </div>
            <div>
              {formatPaymentMethod(order.intended_payment_method)}
            </div>
          </span>
        {/if}
      </div>
    {/if}
    <NotesAndBackup {order} />
  </div>
{/await}
